import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getUrlFilterMode,
  getUrlFilterMultiValues,
  getUrlFilterSlugValue,
  parseUrlActiveFilters,
} from "@/lib/url-active-filters";
import type { SpyserpSegmentFilterOption } from "@/hooks/useSpyserpSegments";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FilterSection } from "@/components/clicks/FiltersSections";
import { Filter, PieChart } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSegmentColorBgClass } from "@/constants/segmentColors";
import type { SpyserpFilterOptions } from "@/types/spyserp";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SpySerpFiltersSidebarProps {
  availableOptions: SpyserpFilterOptions;
  segments: SpyserpSegmentFilterOption[];
  showSlugFilter?: boolean;
}

export function SpySerpFiltersSidebar({
  availableOptions,
  segments,
  showSlugFilter = false,
}: SpySerpFiltersSidebarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filtersParam = searchParams.get("filters");

  const currentFilters = useMemo(
    () => parseUrlActiveFilters(filtersParam),
    [filtersParam],
  );

  const urlSlugValue = getUrlFilterSlugValue(currentFilters.slug);
  const urlSlugMode = getUrlFilterMode(currentFilters.slug);

  const [slugInput, setSlugInput] = useState(urlSlugValue);
  const [slugMode, setSlugMode] = useState<"include" | "exclude">(urlSlugMode);

  useEffect(() => {
    setSlugInput(urlSlugValue);
    setSlugMode(urlSlugMode);
  }, [urlSlugValue, urlSlugMode]);

  const updateFilter = (
    key: string,
    values: string[],
    mode: "include" | "exclude",
  ) => {
    const newFilters = { ...currentFilters };

    if (values.length === 0 && mode === "include") {
      delete newFilters[key];
    } else {
      newFilters[key] = { mode, values };
    }

    setSearchParams((prev) => {
      if (Object.keys(newFilters).length === 0) prev.delete("filters");
      else prev.set("filters", JSON.stringify(newFilters));
      prev.set("page", "1");
      return prev;
    });
  };

  const handleApplySlug = () => {
    const newFilters = { ...currentFilters };

    if (!slugInput.trim()) {
      delete newFilters.slug;
    } else {
      newFilters.slug = { mode: slugMode, value: slugInput.trim() };
    }

    setSearchParams((prev) => {
      if (Object.keys(newFilters).length === 0) prev.delete("filters");
      else prev.set("filters", JSON.stringify(newFilters));
      prev.set("page", "1");
      return prev;
    });
  };

  return (
    <div className="w-64 h-full p-4 space-y-6 bg-card border-r flex flex-col overflow-y-auto custom-scrollbar">
      <h2 className="font-semibold text-lg flex items-center gap-2 px-1">
        <Filter className="w-4 h-4" /> Filters
      </h2>

      {segments.length > 0 && (
        <Accordion
          type="multiple"
          defaultValue={["segments"]}
          className="w-full mb-2"
        >
          <AccordionItem value="segments" className="border-none">
            <AccordionTrigger className="mb-2 cursor-pointer rounded-md bg-accent px-2 py-3 text-sm font-semibold text-accent-foreground hover:no-underline">
              <div className="flex items-center gap-2">
                <PieChart className="w-4 h-4" /> Segments
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-0 px-2">
              <div className="space-y-3">
                <Select
                  value={getUrlFilterMode(currentFilters.segment)}
                  onValueChange={(m: "include" | "exclude") =>
                    updateFilter(
                      "segment",
                      getUrlFilterMultiValues(currentFilters.segment),
                      m,
                    )
                  }
                >
                  <SelectTrigger className="w-full h-8 text-xs bg-background">
                    <SelectValue placeholder="Mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="include">Include</SelectItem>
                    <SelectItem value="exclude">Exclude</SelectItem>
                  </SelectContent>
                </Select>

                <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                  {segments.map((seg) => {
                    const segmentValues = getUrlFilterMultiValues(
                      currentFilters.segment,
                    );
                    const isSelected = segmentValues.includes(seg.id);
                    return (
                      <div key={seg.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`seg-${seg.id}`}
                          checked={isSelected}
                          onChange={(e) => {
                            const newValues = e.target.checked
                              ? [...segmentValues, seg.id]
                              : segmentValues.filter((id) => id !== seg.id);
                            updateFilter(
                              "segment",
                              newValues,
                              getUrlFilterMode(currentFilters.segment),
                            );
                          }}
                          className="h-3.5 w-3.5 cursor-pointer rounded border-border text-primary focus:ring-primary"
                        />
                        <label
                          htmlFor={`seg-${seg.id}`}
                          className="text-xs flex items-center gap-2 cursor-pointer select-none font-medium"
                        >
                          <div
                            className={cn(
                              "w-2.5 h-2.5 rounded-full shadow-sm",
                              getSegmentColorBgClass(seg.color),
                            )}
                          />
                          {seg.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <Accordion
        type="multiple"
        defaultValue={["core"]}
        className="w-full mb-2"
      >
        <AccordionItem value="core" className="border-none">
          <AccordionTrigger className="cursor-pointer text-sm font-semibold hover:no-underline px-2 py-3 bg-muted/50 rounded-md mb-2">
            SpySerp Parameters
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-0">
            <Accordion type="multiple" className="w-full space-y-2">
              <AccordionItem value="keyword" className="border rounded-md px-3">
                <AccordionTrigger className="text-sm py-3 hover:no-underline cursor-pointer">
                  Keyword
                </AccordionTrigger>
                <AccordionContent>
                  <FilterSection
                    title="keyword"
                    options={availableOptions.keywords || []}
                    selectedValues={getUrlFilterMultiValues(currentFilters.keyword)}
                    mode={getUrlFilterMode(currentFilters.keyword)}
                    onChange={(vals, m) => updateFilter("keyword", vals, m)}
                  />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="domain" className="border rounded-md px-3">
                <AccordionTrigger className="text-sm py-3 hover:no-underline cursor-pointer">
                  Domain
                </AccordionTrigger>
                <AccordionContent>
                  <FilterSection
                    title="domain"
                    options={availableOptions.domains || []}
                    selectedValues={getUrlFilterMultiValues(currentFilters.domain)}
                    mode={getUrlFilterMode(currentFilters.domain)}
                    onChange={(vals, m) => updateFilter("domain", vals, m)}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="multiple" className="w-full space-y-2">
        <AccordionItem value="geo" className="border rounded-md px-3">
          <AccordionTrigger className="cursor-pointer text-sm font-semibold hover:no-underline py-3">
            GEO
          </AccordionTrigger>
          <AccordionContent>
            <FilterSection
              title="location"
              options={availableOptions.locations || []}
              selectedValues={getUrlFilterMultiValues(currentFilters.location)}
              mode={getUrlFilterMode(currentFilters.location)}
              onChange={(vals, m) => updateFilter("location", vals, m)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="lang" className="border rounded-md px-3">
          <AccordionTrigger className="cursor-pointer text-sm font-semibold hover:no-underline py-3">
            Language
          </AccordionTrigger>
          <AccordionContent>
            <FilterSection
              title="lang_page"
              options={availableOptions.languages || []}
              selectedValues={getUrlFilterMultiValues(currentFilters.lang_page)}
              mode={getUrlFilterMode(currentFilters.lang_page)}
              onChange={(vals, m) => updateFilter("lang_page", vals, m)}
            />
          </AccordionContent>
        </AccordionItem>

        {showSlugFilter && (
          <AccordionItem value="slug" className="border rounded-md px-3">
            <AccordionTrigger className="cursor-pointer text-sm font-semibold hover:no-underline py-3">
              URL Slug
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pb-3">
              <Select
                value={slugMode}
                onValueChange={(v: "include" | "exclude") => setSlugMode(v)}
              >
                <SelectTrigger className="w-full h-8 text-xs">
                  <SelectValue placeholder="Mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="include">Include</SelectItem>
                  <SelectItem value="exclude">Exclude</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="e.g. /de-at/"
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApplySlug()}
                className="h-8 text-xs"
              />

              <Button
                onClick={handleApplySlug}
                size="sm"
                variant="secondary"
                className="w-full h-8 text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Apply Filter
              </Button>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}
